import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

const CHECK_INTERVAL_MS = 60_000;
const RELOAD_GUARD_KEY = "sisthoko_forced_bundle_refresh";

function isPreviewOrIframe() {
  if (typeof window === "undefined") return true;
  let inIframe = false;
  try {
    inIframe = window.self !== window.top;
  } catch {
    inIframe = true;
  }
  const host = window.location.hostname;
  return inIframe
    || host.startsWith("id-preview--")
    || host.startsWith("preview--")
    || host === "lovableproject.com"
    || host.endsWith(".lovableproject.com")
    || host === "lovableproject-dev.com"
    || host.endsWith(".lovableproject-dev.com")
    || host === "beta.lovable.dev"
    || host.endsWith(".beta.lovable.dev");
}

function getCurrentEntryScriptPath() {
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'));
  const entry = scripts.find((script) => new URL(script.src, window.location.origin).pathname.includes("/assets/index-"));
  return entry ? new URL(entry.src, window.location.origin).pathname : null;
}

function getEntryScriptPathFromHtml(html: string) {
  const match = html.match(/<script[^>]+type=["']module["'][^>]+src=["']([^"']*\/assets\/index-[^"']+\.js)["']/i);
  if (!match?.[1]) return null;
  return new URL(match[1], window.location.origin).pathname;
}

/**
 * Detects when a new PWA bundle is available (new service worker waiting)
 * and prompts the user to refresh. This ensures web-push code and other
 * fixes reach installed users without manual cache clearing.
 *
 * Also runs a periodic update check every 60s while the app is open.
 */
function PWAUpdatePromptInner() {
  const [shown, setShown] = useState(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(swUrl, r) {
      if (!r) return;
      // Poll for an updated SW every minute so long-lived sessions also update.
      const interval = setInterval(() => {
        r.update().catch(() => {});
      }, CHECK_INTERVAL_MS);
      // Best-effort cleanup; component is mounted for app lifetime.
      window.addEventListener("beforeunload", () => clearInterval(interval), { once: true });
    },
  });

  useEffect(() => {
    if (isPreviewOrIframe()) return;

    let cancelled = false;
    const checkForNewEntryBundle = async () => {
      try {
        const currentEntry = getCurrentEntryScriptPath();
        if (!currentEntry) return;

        const res = await fetch(`${window.location.origin}/?bundle-check=${Date.now()}`, {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });
        if (!res.ok) return;
        const latestEntry = getEntryScriptPathFromHtml(await res.text());
        if (!latestEntry || latestEntry === currentEntry || cancelled) return;

        const guardValue = `${currentEntry}->${latestEntry}`;
        if (sessionStorage.getItem(RELOAD_GUARD_KEY) === guardValue) return;
        sessionStorage.setItem(RELOAD_GUARD_KEY, guardValue);

        toast("Updating Sis Thoko", {
          description: "Refreshing now to load the latest notification fixes.",
          duration: 2500,
          icon: <RefreshCw className="h-4 w-4" />,
        });
        setTimeout(() => window.location.reload(), 700);
      } catch {
        // Best-effort stale-bundle detection; never block the app.
      }
    };

    checkForNewEntryBundle();
    const interval = setInterval(checkForNewEntryBundle, CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!needRefresh || shown) return;
    setShown(true);
    toast("A new version of Sis Thoko is ready", {
      description: "Refresh to get the latest fixes and notifications.",
      duration: Infinity,
      icon: <RefreshCw className="h-4 w-4" />,
      action: {
        label: "Refresh",
        onClick: () => updateServiceWorker(true),
      },
    });
  }, [needRefresh, shown, updateServiceWorker]);

  return null;
}

export function PWAUpdatePrompt() {
  if (isPreviewOrIframe()) return null;
  return <PWAUpdatePromptInner />;
}
